"use client";

import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "@workspace/ui/lib/utils";
import { SquarePlay } from "lucide-react";
import Link from "next/link";

type BrowserTabsProps = React.ComponentProps<typeof TabsPrimitive.Root>;

function BrowserTabs({ className, ...props }: BrowserTabsProps) {
  return (
    <TabsPrimitive.Root
      data-slot="browser-tabs"
      className={cn(
        "flex flex-col gap-[18px] rounded-[24px] bg-[#f3e4c9] p-2.5 pt-[18px] w-full max-w-[1120px]",
        className,
      )}
      {...props}
    />
  );
}

type BrowserTabsListProps = React.ComponentProps<typeof TabsPrimitive.List>;

function BrowserTabsList({ className, ...props }: BrowserTabsListProps) {
  return (
    <div className="flex items-center pl-[22px] gap-6 w-full">
      <div className="flex items-center gap-3">
        <span className="size-3 lg:size-4 bg-[#ed5931] rounded-full" />
        <span className="size-3 lg:size-4 bg-[#FFAD62] rounded-full" />
        <span className="size-3 lg:size-4 bg-[#C9B829] rounded-full" />
      </div>
      <TabsPrimitive.List
        data-slot="browser-tabs-list"
        className={cn(
          "flex items-center gap-2 overflow-x-auto overflow-y-hidden",
          className,
        )}
        {...props}
      />
    </div>
  );
}

type BrowserTabProps = React.ComponentProps<typeof TabsPrimitive.Trigger> & {
  icon?: React.ReactNode;
  href?: string;
};

function BrowserTab({
  className,
  children,
  icon,
  href,
  ...props
}: BrowserTabProps) {
  const tabClassName = cn(
    "flex items-center h-11 bg-white rounded-[10px] gap-2.5 px-3 cursor-pointer",
    "data-[state=inactive]:bg-white/50 data-[state=inactive]:opacity-70",
    "transition-all duration-200",
    className,
  );

  const content = (
    <>
      {icon || (
        <SquarePlay className="[&>rect]:fill-d-main [&>rect]:stroke-d-main [&>path]:fill-black" />
      )}
      <span className="text-black font-semibold">{children}</span>
    </>
  );

  if (href) {
    return (
      <TabsPrimitive.Trigger
        data-slot="browser-tab"
        className={tabClassName}
        asChild
        {...props}
      >
        <Link href={href} scroll={false}>{content}</Link>
      </TabsPrimitive.Trigger>
    );
  }

  return (
    <TabsPrimitive.Trigger
      data-slot="browser-tab"
      className={tabClassName}
      {...props}
    >
      {content}
    </TabsPrimitive.Trigger>
  );
}

type BrowserTabContentProps = React.ComponentProps<
  typeof TabsPrimitive.Content
>;

function BrowserTabContent({ className, ...props }: BrowserTabContentProps) {
  return (
    <TabsPrimitive.Content
      data-slot="browser-tab-content"
      className={cn(
        "relative flex aspect-video w-full bg-white rounded-[20px] border border-[#e3b8c4] overflow-hidden",
        className,
      )}
      {...props}
    />
  );
}

export { BrowserTabs, BrowserTabsList, BrowserTab, BrowserTabContent };
